/**
 * Supplier-hub surfaces for PartyDetailPage: the per-currency KPI header, the
 * Purchases tab (orders + bills in one list), the Payments tab, and the
 * in-page "record payment" dialog that allocates across open bills.
 *
 * Money rule: every figure is grouped BY CURRENCY and formatted with
 * formatMoney (backend-driven decimals — IQD 0dp, USD 2dp). Nothing is ever
 * summed or converted across currencies on the client.
 */
import type { Party, PortalStatement } from '@erp/shared';
import Decimal from 'decimal.js';
import {
  Banknote, CalendarClock, FileText, HandCoins, Package, Receipt, Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Badge, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Skeleton,
} from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Button, Card, EmptyState, FormField, Input, StatCard } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney, getCurrencyDecimals, trimMoney } from '../../lib/money.js';
import { normalizeAmountTyping, safeDecimal } from '../../lib/numerals.js';

/** A purchase document header as the generic document list serializes it. */
export interface PurchaseDocRow {
  id: string;
  docNumber: string;
  docDate: string;
  dueDate?: string | null;
  status: string;
  currencyCode: string;
  total: string;
  /** Bills only — purchase orders don't track payment. */
  amountPaid?: string;
  isDebitNote?: boolean;
}

export interface SupplierPaymentRow {
  id: string;
  docNumber: string;
  paymentDate: string;
  direction: 'inbound' | 'outbound';
  status: string;
  currencyCode: string;
  amount: string;
  method?: string | null;
  reference?: string | null;
}

/** A bill with something still owed on it — what a payment can be allocated to. */
export interface OpenBill {
  id: string;
  docNumber: string;
  docDate: string;
  currencyCode: string;
  balance: string;
}

/** Documents pages cap at 500; a party's own history stays well inside that. */
const DOC_LIMIT = 500;

const money = (amount: string, currency: string, locale: string): string =>
  formatMoney(amount, { locale, currency });

/** Bills that are posted (AP recognised) and not yet settled, oldest first. */
export function openBillsFrom(bills: PurchaseDocRow[]): OpenBill[] {
  return bills
    // Debit notes are supplier CREDITS, not something we pay — allocating cash
    // to one would corrupt AP. Drafts/cancelled bills have no AP entry yet.
    .filter((b) => !b.isDebitNote && (b.status === 'approved' || b.status === 'paid'))
    .map((b) => ({
      id: b.id,
      docNumber: b.docNumber,
      docDate: b.docDate,
      currencyCode: b.currencyCode,
      balance: new Decimal(b.total || '0').minus(b.amountPaid ?? '0').toFixed(4),
    }))
    .filter((b) => new Decimal(b.balance).gt('0.0001'))
    .sort((a, b) => (a.docDate < b.docDate ? -1 : a.docDate > b.docDate ? 1 : a.docNumber < b.docNumber ? -1 : 1));
}

/**
 * The one place a supplier's purchase documents are loaded. The KPI strip, the
 * Purchases tab, the Payments tab and both "record payment" dialogs all read
 * from this single snapshot, so they can never show each other's stale numbers
 * — and the browser fetches each list once per page, not once per tab visit.
 *
 * `refreshKey` is bumped by the page after anything that moves money.
 */
export interface SupplierDocs {
  bills: PurchaseDocRow[];
  /** Posted, still-owed bills, oldest first — what a payment can settle. */
  openBills: OpenBill[];
  loading: boolean;
  canRead: boolean;
}

export function useSupplierDocs(partyId: string | null, refreshKey: number): SupplierDocs {
  const canReadBill = usePermission('bills.read');
  const [bills, setBills] = useState<PurchaseDocRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Disabled (customer-only party), or no access: settle into a
    // loaded-but-empty state rather than a permanent skeleton.
    if (!partyId || !canReadBill) { setBills([]); setLoading(false); return undefined; }
    let active = true;
    setLoading(true);
    void api()
      .get<{ items: PurchaseDocRow[] }>('/bills', { params: { partyId, limit: DOC_LIMIT } })
      .then((r) => r.data.items)
      .catch(() => [] as PurchaseDocRow[])
      .then((bl) => {
        if (!active) return;
        setBills(bl);
        setLoading(false);
      });
    return () => { active = false; };
  }, [partyId, refreshKey, canReadBill]);

  const openBills = useMemo(() => openBillsFrom(bills), [bills]);
  return { bills, openBills, loading, canRead: canReadBill };
}

function statusVariant(status: string): 'success' | 'secondary' | 'warning' | 'info' {
  if (status === 'paid' || status === 'posted' || status === 'received') return 'success';
  if (status === 'cancelled' || status === 'voided') return 'secondary';
  if (status === 'draft') return 'info';
  return 'warning';
}

// ---------------------------------------------------------------- KPI header

/**
 * Vendor KPI strip: what we owe per currency, what we've been billed, how many
 * bills are still open and when the last purchase was. Driven by the vendor
 * statement (bills with status approved/paid + posted outbound payments), so it
 * agrees with the Supplier-account tab by construction.
 */
export function SupplierKpiHeader({
  statement, loading,
}: {
  statement: PortalStatement | null;
  loading: boolean;
}): JSX.Element {
  const { t } = useTranslation('parties');
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const owed = (statement?.summary ?? []).filter((s) => new Decimal(s.balance).gt('0.0001'));
  // An advance we paid shows too (a negative balance), not just what we owe.
  const balances = (statement?.summary ?? []).filter((s) => new Decimal(s.balance).abs().gt('0.0001'));
  const openCount = (statement?.invoices ?? []).filter((i) => new Decimal(i.balance).gt('0.0001')).length;
  // The statement returns bills newest-first, so the head row is the last
  // purchase we've actually recognised (draft bills aren't purchases yet).
  const lastPurchase = statement?.invoices[0]?.docDate ?? null;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label={t('supplier.kpi.weOwe', { defaultValue: 'We owe' })}
        icon={Wallet}
        tone={owed.length ? 'warning' : 'success'}
        loading={loading}
        value={
          balances.length === 0 ? (
            <span className="text-success">{t('supplier.kpi.settled', { defaultValue: 'Settled' })}</span>
          ) : (
            <span className="flex flex-col gap-0.5">
              {balances.map((s) => {
                const advance = new Decimal(s.balance).lt(0);
                return (
                  <span key={s.currencyCode} className={`font-mono tabular-nums ${advance ? 'text-success' : ''}`}>
                    {money(s.balance, s.currencyCode, locale)}
                    {advance && <span className="ms-1.5 font-sans text-xs font-semibold">({t('supplier.kpi.advance', { defaultValue: 'advance paid' })})</span>}
                  </span>
                );
              })}
            </span>
          )
        }
        hint={balances.length > 1 ? t('supplier.kpi.perCurrency', { defaultValue: 'Per currency' }) : undefined}
      />
      <StatCard
        label={t('supplier.kpi.billed', { defaultValue: 'Billed' })}
        icon={Receipt}
        tone="brand"
        loading={loading}
        value={
          (statement?.summary ?? []).length === 0 ? (
            <span className="text-fg-muted">—</span>
          ) : (
            <span className="flex flex-col gap-0.5">
              {statement!.summary.map((s) => (
                <span key={s.currencyCode} className="font-mono tabular-nums">{money(s.invoiced, s.currencyCode, locale)}</span>
              ))}
            </span>
          )
        }
      />
      <StatCard
        label={t('supplier.kpi.openBills', { defaultValue: 'Open bills' })}
        icon={FileText}
        tone={openCount ? 'warning' : 'neutral'}
        loading={loading}
        value={<span className="font-mono tabular-nums">{openCount}</span>}
      />
      <StatCard
        label={t('supplier.kpi.lastPurchase', { defaultValue: 'Last purchase' })}
        icon={CalendarClock}
        tone="info"
        loading={loading}
        value={lastPurchase ? formatDate(lastPurchase, { locale }) : <span className="text-fg-muted">—</span>}
      />
    </div>
  );
}

// ------------------------------------------------------------ Purchases tab

/**
 * Every purchase order AND bill for this supplier, newest first, with the
 * balance still owed on each bill. Fetched with the server-side `partyId`
 * filter (added alongside this page) rather than by pulling the whole
 * tenant's documents and filtering in the browser.
 */
export function SupplierPurchasesTab({ party, docs }: { party: Party; docs: SupplierDocs }): JSX.Element {
  const { t } = useTranslation('parties');
  const { t: tc, i18n } = useTranslation();
  const locale = i18n.language;
  const canWriteBill = usePermission('bills.write');
  const { bills, loading } = docs;

  // Bills only — a purchase is entered straight onto a bill; there is no order
  // to raise and execute first, so there is nothing else to merge in here.
  const rows = useMemo(
    () => [...bills].sort((a, b) =>
      a.docDate === b.docDate ? (a.docNumber < b.docNumber ? 1 : -1) : a.docDate < b.docDate ? 1 : -1,
    ),
    [bills],
  );

  const actions = (
    <div className="flex flex-wrap gap-2">
      {canWriteBill && (
        <Link to={`/purchasing/bills/new?party=${party.id}`}>
          <Button size="sm">
            <Receipt className="me-2 h-4 w-4" aria-hidden />
            {t('supplier.newBill', { defaultValue: 'New bill' })}
          </Button>
        </Link>
      )}
    </div>
  );

  if (loading) {
    return (
      <Card className="p-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="mt-3 h-40 w-full" />
      </Card>
    );
  }

  if (!docs.canRead) {
    return <Card className="p-6"><EmptyState title={t('supplier.noPermission', { defaultValue: 'You do not have access to purchase documents.' })} /></Card>;
  }

  if (rows.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState
          icon={<Package className="h-8 w-8" aria-hidden />}
          title={t('supplier.purchasesEmpty', { defaultValue: 'No purchases from this supplier yet.' })}
          description={t('supplier.purchasesEmptyHint', { defaultValue: 'Record what you buy from them as a bill.' })}
          action={actions}
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-fg-subtle">
          {t('supplier.purchases', { defaultValue: 'Purchases' })}
          <span className="ms-2 font-normal text-fg-muted">({rows.length})</span>
        </h2>
        {actions}
      </div>
      <Card className="overflow-x-auto p-0">
        <table className="w-full text-start text-sm">
          <thead className="border-b border-border bg-bg-subtle text-fg-muted">
            <tr>
              <th className="px-4 py-2.5 text-start">{t('supplier.columns.document', { defaultValue: 'Document' })}</th>
              <th className="px-4 py-2.5 text-start">{t('supplier.columns.type', { defaultValue: 'Type' })}</th>
              <th className="px-4 py-2.5 text-start">{t('sales.date', { defaultValue: 'Date' })}</th>
              <th className="px-4 py-2.5 text-start">{t('sales.status', { defaultValue: 'Status' })}</th>
              <th className="px-4 py-2.5 text-end">{t('sales.total', { defaultValue: 'Total' })}</th>
              <th className="px-4 py-2.5 text-end">{t('supplier.columns.owed', { defaultValue: 'Still owed' })}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const owed = d.status !== 'cancelled'
                ? new Decimal(d.total || '0').minus(d.amountPaid ?? '0')
                : null;
              return (
                <tr key={d.id} className="border-b border-border/60 last:border-0 hover:bg-bg-subtle/50">
                  <td className="px-4 py-2.5">
                    <Link to={`/purchasing/bills/${d.id}`} className="font-mono font-medium text-brand hover:underline">{d.docNumber}</Link>
                  </td>
                  <td className="px-4 py-2.5 text-fg-muted">
                    {d.isDebitNote
                      ? t('supplier.type.debitNote', { defaultValue: 'Debit note' })
                      : t('supplier.type.bill', { defaultValue: 'Bill' })}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-fg-muted">{formatDate(d.docDate, { locale })}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant={statusVariant(d.status)}>
                      {t(`supplier.statusValue.${d.status}`, { defaultValue: d.status })}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-end font-mono tabular-nums">{money(d.total, d.currencyCode, locale)}</td>
                  <td className={`px-4 py-2.5 text-end font-mono tabular-nums ${owed && owed.gt('0.0001') ? 'font-semibold text-warning' : 'text-fg-muted'}`}>
                    {owed ? money(owed.toFixed(4), d.currencyCode, locale) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-fg-muted">{tc('columns.currency', { defaultValue: 'Currency' })}: {t('supplier.perDocumentCurrency', { defaultValue: 'each row shows its own document currency — totals are never combined.' })}</p>
    </div>
  );
}

// ------------------------------------------------------------- Payments tab

/**
 * Money paid to this supplier, plus the in-page "Record payment" dialog. The
 * dialog creates AND posts the payment (an unposted payment does not reduce
 * what we owe), then refreshes this tab and the page's balance.
 */
export function SupplierPaymentsTab({
  party, docs, refreshKey, onRecordPayment,
}: {
  party: Party;
  docs: SupplierDocs;
  refreshKey: number;
  /** Null when the user may not record payments — the page owns the dialog. */
  onRecordPayment: (() => void) | null;
}): JSX.Element {
  const { t } = useTranslation('parties');
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const canRead = usePermission('payments.read');
  const [payments, setPayments] = useState<SupplierPaymentRow[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canRead) { setPayments([]); setLoading(false); return undefined; }
    let active = true;
    setLoading(true);
    // `direction=outbound` is filtered SERVER-side: filtering here instead would
    // let a 'both' party's inbound receipts fill the 500-row page and hide every
    // supplier payment behind an empty state.
    void api()
      .get<{ items: SupplierPaymentRow[] }>('/payments', {
        params: { partyId: party.id, direction: 'outbound', limit: DOC_LIMIT },
      })
      .then((r) => { if (active) setPayments(r.data.items); })
      .catch(() => { if (active) setPayments([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [party.id, refreshKey, canRead]);

  const openBills = docs.openBills;

  const payButton = onRecordPayment ? (
    <Button size="sm" onClick={onRecordPayment}>
      <HandCoins className="me-2 h-4 w-4" aria-hidden />
      {t('account.recordPayment', { defaultValue: 'Record payment' })}
    </Button>
  ) : null;

  if (loading) {
    return (
      <Card className="p-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="mt-3 h-40 w-full" />
      </Card>
    );
  }

  if (!payments || payments.length === 0) {
    return (
      <>
        <Card className="p-6">
          <EmptyState
            icon={<Banknote className="h-8 w-8" aria-hidden />}
            title={t('supplier.paymentsEmpty', { defaultValue: 'No payments to this supplier yet.' })}
            description={
              openBills.length > 0
                ? t('supplier.paymentsEmptyOpen', { defaultValue: 'There are open bills waiting to be settled.' })
                : t('supplier.paymentsEmptyHint', { defaultValue: 'Payments recorded here reduce what you owe.' })
            }
            action={payButton ?? undefined}
          />
        </Card>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-fg-subtle">
          {t('tabs.payments', { defaultValue: 'Payments' })}
          <span className="ms-2 font-normal text-fg-muted">({payments.length})</span>
        </h2>
        {payButton}
      </div>
      <Card className="overflow-x-auto p-0">
        <table className="w-full text-start text-sm">
          <thead className="border-b border-border bg-bg-subtle text-fg-muted">
            <tr>
              <th className="px-4 py-2.5 text-start">{t('payments.number', { defaultValue: 'Number' })}</th>
              <th className="px-4 py-2.5 text-start">{t('payments.date', { defaultValue: 'Date' })}</th>
              <th className="px-4 py-2.5 text-start">{t('payments.method', { defaultValue: 'Method' })}</th>
              <th className="px-4 py-2.5 text-start">{t('sales.status', { defaultValue: 'Status' })}</th>
              <th className="px-4 py-2.5 text-end">{t('payments.amount', { defaultValue: 'Amount' })}</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-border/60 last:border-0 hover:bg-bg-subtle/50">
                <td className="px-4 py-2.5 font-mono font-medium">{p.docNumber}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-fg-muted">{formatDate(p.paymentDate, { locale })}</td>
                <td className="px-4 py-2.5 text-fg-muted">
                  {p.method ? t(`supplier.method.${p.method}`, { defaultValue: p.method }) : '—'}
                </td>
                <td className="px-4 py-2.5">
                  <Badge variant={statusVariant(p.status)}>
                    {t(`supplier.statusValue.${p.status}`, { defaultValue: p.status })}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 text-end font-mono font-semibold tabular-nums">
                  {money(p.amount, p.currencyCode, locale)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------- Payment dialog

/**
 * Record + post an outbound payment without leaving the page. Allocations
 * default to oldest-bill-first for the entered amount and stay editable; the
 * bill list is restricted to the chosen currency because amount_paid
 * accumulates raw amounts (the server rejects a cross-currency allocation).
 */
export function SupplierPaymentDialog({
  party, openBills, defaultCurrency, onClose, onDone,
}: {
  party: Party;
  openBills: OpenBill[];
  defaultCurrency?: string;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation('parties');
  const { t: tc, i18n } = useTranslation();
  const locale = i18n.language;
  // Currencies this supplier actually has open bills in — the useful choices.
  const billCurrencies = useMemo(
    () => [...new Set(openBills.map((b) => b.currencyCode))],
    [openBills],
  );
  const [currency, setCurrency] = useState(
    (defaultCurrency ?? billCurrencies[0] ?? 'USD').toUpperCase(),
  );
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  /** billId → allocated amount (string, editable). Absent = not allocated. */
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [touchedAlloc, setTouchedAlloc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allocatable = useMemo(
    () => openBills.filter((b) => b.currencyCode === currency),
    [openBills, currency],
  );

  const dp = getCurrencyDecimals(currency) ?? 2;
  const fmt = useCallback((v: string): string => money(v, currency, locale), [currency, locale]);

  /** Spread `total` over the open bills oldest-first, capped at each balance. */
  const autoAllocate = useCallback((total: string): Record<string, string> => {
    let left = safeDecimal(total);
    const out: Record<string, string> = {};
    for (const b of allocatable) {
      if (left.lte(0)) break;
      const balance = new Decimal(b.balance);
      const take = Decimal.min(left, balance);
      if (take.lte(0)) continue;
      // Balances are stored at 4dp but displayed at the currency's precision
      // (IQD = 0). Rounding to `dp` HALF-UP could produce an allocation larger
      // than the bill owes, which trips the over-allocation guard with no way
      // to fix it in a 0-decimal field. Settling in full uses the EXACT
      // balance; a partial allocation rounds DOWN.
      out[b.id] = take.eq(balance) ? trimMoney(balance.toFixed(4)) : take.toDecimalPlaces(dp, Decimal.ROUND_DOWN).toFixed(dp);
      left = left.minus(take);
    }
    return out;
  }, [allocatable, dp]);

  // Re-spread while the user hasn't hand-edited an allocation; once they have,
  // their split is theirs to keep. `autoAllocate` changes with the currency
  // (via `allocatable`), so switching currency re-spreads here too — which is
  // why the reset lives in the currency handler and NOT in a second effect: an
  // effect keyed on [currency] runs AFTER this one in the same commit and would
  // wipe the allocations it had just computed, leaving the payment unallocated.
  useEffect(() => {
    if (touchedAlloc) return;
    setAlloc(autoAllocate(amount));
  }, [amount, autoAllocate, touchedAlloc]);

  /** Switching currency invalidates every allocation — they were against bills
   *  in the old currency — so drop them and let the effect above re-derive. */
  const onCurrencyChange = (next: string): void => {
    setTouchedAlloc(false);
    setAlloc({});
    setCurrency(next);
  };

  // Every one of these reads a raw INPUT value, so it must go through
  // safeDecimal — decimal.js throws on a half-typed or Arabic-digit string and
  // would unmount the dialog mid-keystroke (see lib/numerals.ts).
  const allocTotal = useMemo(
    () => Object.values(alloc).reduce((s, v) => s.plus(safeDecimal(v)), new Decimal(0)),
    [alloc],
  );
  const amountDec = safeDecimal(amount);
  const overAllocated = allocTotal.gt(amountDec);
  const unallocated = amountDec.minus(allocTotal);
  const perBillOver = allocatable.filter((b) => safeDecimal(alloc[b.id]).gt(b.balance));

  const fillOutstanding = (): void => {
    // Exact sum of the stored balances — rounding it up to `dp` would leave a
    // few fils permanently "on account" instead of settling the bills.
    const total = allocatable.reduce((s, b) => s.plus(b.balance), new Decimal(0));
    setTouchedAlloc(false);
    setAmount(trimMoney(total.toFixed(4)));
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (amountDec.lte(0)) {
      setError(t('supplier.pay.errors.amount', { defaultValue: 'Enter an amount greater than zero.' }));
      return;
    }
    if (overAllocated) {
      setError(t('supplier.pay.errors.overAllocated', { defaultValue: 'Allocated more than the payment amount.' }));
      return;
    }
    if (perBillOver.length > 0) {
      setError(t('supplier.pay.errors.overBill', { defaultValue: 'A bill is allocated more than it still owes.' }));
      return;
    }
    setBusy(true);
    try {
      const allocations = Object.entries(alloc)
        .filter(([, v]) => safeDecimal(v).gt(0))
        .map(([targetId, v]) => ({ targetType: 'bill' as const, targetId, amount: safeDecimal(v).toFixed(4) }));
      // `post: true` creates AND posts in one server transaction. Posting is
      // what actually moves AP and each bill's amount_paid, so splitting it
      // across two calls could leave a draft payment that reduces nothing.
      await api().post<{ id: string; posted: boolean }>('/payments', {
        paymentDate,
        direction: 'outbound',
        partyId: party.id,
        currencyCode: currency,
        amount: amountDec.toFixed(4),
        method: method.trim() || undefined,
        ...(allocations.length ? { allocations } : {}),
        post: true,
      });
      toast.success(t('account.paymentRecorded', { defaultValue: 'Payment recorded' }));
      onClose();
      onDone();
    } catch (err) {
      // Structured sub-codes arrive on `cause.code`; `code` is the top-level
      // problem type (see apps/api/src/middleware/errorHandler.ts).
      const data = (err as { response?: { data?: { code?: string; cause?: { code?: string } } } }).response?.data;
      const code = data?.cause?.code ?? data?.code;
      setError(
        code === 'allocation_currency_mismatch'
          ? t('account.allocationCurrencyMismatch', { defaultValue: 'The payment currency must match the invoice it settles.' })
          : code === 'allocations_exceed_amount'
            ? t('supplier.pay.errors.overAllocated', { defaultValue: 'Allocated more than the payment amount.' })
            : code === 'currency.rate_not_found'
              ? tc('errors:currency.rate_not_found')
              : code === 'auth.forbidden'
                ? t('supplier.pay.errors.forbidden', { defaultValue: 'You do not have permission to post payments.' })
                : tc('errors:server'),
      );
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              {t('supplier.pay.title', { defaultValue: 'Pay supplier' })} — {party.displayName}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3 py-3 sm:grid-cols-2 lg:grid-cols-4">
            <FormField id="sp-amount" label={t('account.amount', { defaultValue: 'Amount' })} required>
              <Input
                id="sp-amount"
                inputMode="decimal"
                value={amount}
                // Store the ASCII form so Arabic/Persian digits never reach the API.
                onChange={(e) => { setTouchedAlloc(false); setAmount(normalizeAmountTyping(e.target.value)); }}
                autoFocus
                required
              />
            </FormField>
            <FormField id="sp-ccy" label={tc('columns.currency', { defaultValue: 'Currency' })}>
              <CurrencySelect id="sp-ccy" value={currency} onChange={onCurrencyChange} />
            </FormField>
            <FormField id="sp-method" label={t('account.method', { defaultValue: 'Method' })}>
              <select
                id="sp-method"
                className="h-9 w-full rounded-md border border-border bg-bg px-2 text-sm text-fg"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              >
                <option value="cash">{t('supplier.method.cash', { defaultValue: 'Cash' })}</option>
                <option value="bank">{t('supplier.method.bank', { defaultValue: 'Bank transfer' })}</option>
                <option value="card">{t('supplier.method.card', { defaultValue: 'Card' })}</option>
                <option value="cheque">{t('supplier.method.cheque', { defaultValue: 'Cheque' })}</option>
              </select>
            </FormField>
            <FormField id="sp-date" label={t('payments.date', { defaultValue: 'Date' })}>
              <Input id="sp-date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
            </FormField>
          </div>

          <div className="rounded-lg border border-border">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-bg-subtle px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {t('supplier.pay.allocate', { defaultValue: 'Apply to open bills' })}
              </span>
              {allocatable.length > 0 && (
                <Button type="button" variant="ghost" size="sm" onClick={fillOutstanding}>
                  {t('supplier.pay.payAll', { defaultValue: 'Pay everything outstanding' })}
                </Button>
              )}
            </div>
            {allocatable.length === 0 ? (
              <p className="px-3 py-4 text-sm text-fg-muted">
                {openBills.length === 0
                  ? t('supplier.pay.noOpenBills', { defaultValue: 'No open bills — this will be recorded on account.' })
                  : t('supplier.pay.noOpenBillsInCurrency', {
                    currency,
                    defaultValue: 'No open bills in {{currency}} — this will be recorded on account.',
                  })}
              </p>
            ) : (
              <ul className="max-h-56 divide-y divide-border overflow-auto">
                {allocatable.map((b) => {
                  const v = alloc[b.id] ?? '';
                  const over = safeDecimal(v).gt(b.balance);
                  return (
                    <li key={b.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <Link to={`/purchasing/bills/${b.id}`} className="font-mono text-sm text-brand hover:underline">
                          {b.docNumber}
                        </Link>
                        <div className="text-xs text-fg-muted">
                          {formatDate(b.docDate, { locale })} · {t('supplier.pay.owed', { defaultValue: 'owed' })} {fmt(b.balance)}
                        </div>
                      </div>
                      <Input
                        aria-label={`${t('supplier.pay.allocated', { defaultValue: 'Allocated' })} ${b.docNumber}`}
                        className="w-32 text-end font-mono"
                        inputMode="decimal"
                        invalid={over}
                        value={v}
                        onChange={(e) => {
                          setTouchedAlloc(true);
                          setAlloc((prev) => ({ ...prev, [b.id]: normalizeAmountTyping(e.target.value) }));
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-3 py-2 text-sm">
              <span className="text-fg-muted">{t('supplier.pay.allocated', { defaultValue: 'Allocated' })}</span>
              <span className={`font-mono tabular-nums ${overAllocated ? 'font-semibold text-danger' : 'text-fg'}`}>
                {fmt(allocTotal.toFixed(dp))}
                {unallocated.gt('0.0001') && (
                  <span className="ms-2 text-xs font-normal text-fg-muted">
                    {t('supplier.pay.onAccount', { defaultValue: 'on account' })} {fmt(unallocated.toFixed(dp))}
                  </span>
                )}
              </span>
            </div>
          </div>

          {error && <p className="mt-3 text-sm text-danger" role="alert">{error}</p>}

          <DialogFooter className="mt-4">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>{tc('actions.cancel')}</Button>
            <Button type="submit" loading={busy} disabled={amountDec.lte(0) || overAllocated || perBillOver.length > 0}>
              {t('supplier.pay.submit', { defaultValue: 'Record & post payment' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
